// videoMask.ts — 영상 영역 모자이크(고정/키프레임 따라가기). 의존성 0.
//   ★ gif-masker(hislegend/gif-masker lib.js) 코어를 TS로 이식. Mediabunny 프레임 루프에 꽂아 쓴다.
//   좌표는 "그려진 소스 영상 rect 기준 정규화(0..1)". 렌더 쪽에서 fit-rect로 매핑해 적용.

import type {MaskKeyframe,MaskRegion} from '../../apps/precision-editor/protocol/mask-region';
export type {MaskKeyframe,MaskSpace,MaskRegion} from '../../apps/precision-editor/protocol/mask-region';

interface NRect { x: number; y: number; w: number; h: number; }

// 정규화 사각형(0..1) → 픽셀 정수 사각형(오프셋 ox,oy + 크기 rw,rh 기준), 경계 클램프
export function normToPx(rect: NRect, ox: number, oy: number, rw: number, rh: number): { x: number; y: number; w: number; h: number } {
  let x = Math.round(ox + rect.x * rw), y = Math.round(oy + rect.y * rh);
  let w = Math.round(rect.w * rw), h = Math.round(rect.h * rh);
  if (w < 0) { x += w; w = -w; }
  if (h < 0) { y += h; h = -h; }
  const maxX = ox + rw, maxY = oy + rh;
  x = Math.max(ox, Math.min(maxX, x)); y = Math.max(oy, Math.min(maxY, y));
  w = Math.max(0, Math.min(maxX - x, w)); h = Math.max(0, Math.min(maxY - y, h));
  return { x, y, w, h };
}

// 키프레임 선형보간 → 시각 t(초)에서의 정규화 사각형. keyframes는 t 오름차순.
export function interpKeyframes(k: MaskKeyframe[], t: number): NRect | null {
  if (!k || k.length === 0) return null;
  const pick = (r: MaskKeyframe): NRect => ({ x: r.x, y: r.y, w: r.w, h: r.h });
  if (t <= k[0].t) return pick(k[0]);
  if (t >= k[k.length - 1].t) return pick(k[k.length - 1]);
  let i = 0;
  while (i < k.length - 1 && !(k[i].t <= t && t < k[i + 1].t)) i++;
  const a = k[i], b = k[i + 1];
  const span = b.t - a.t;
  const f = span <= 0 ? 0 : (t - a.t) / span;
  return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, w: a.w + (b.w - a.w) * f, h: a.h + (b.h - a.h) * f };
}

// 시각 t 에서 한 영역의 정규화 사각형(없으면 null = 이 프레임엔 적용 안 함)
export function regionRectAt(region: MaskRegion, t: number): NRect | null {
  if (region.type === "fixed") {
    if ((region.start != null && t < region.start) || (region.end != null && t > region.end)) return null;
    return { ...region.rect };
  }
  if (region.type === "track") return interpKeyframes(region.keyframes, t);
  return null;
}

// 모자이크(픽셀화): 영역 축소→블록 확대. gif-masker pixelate 이식.
export function pixelate(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, ps: number, strict=false) {
  if (w < 1 || h < 1) return;
  const sw = Math.max(1, Math.floor(w / ps)), sh = Math.max(1, Math.floor(h / ps));
  const tmp = document.createElement("canvas"); tmp.width = sw; tmp.height = sh;
  const tc = tmp.getContext("2d");
  if (!tc) { if(strict)throw new Error("모자이크 처리 화면을 만들지 못했습니다."); return; }
  tc.imageSmoothingEnabled = true;
  tc.drawImage(ctx.canvas, x, y, w, h, 0, 0, sw, sh); // 축소(평균)
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(tmp, 0, 0, sw, sh, x, y, w, h);       // 확대(블록)
  ctx.imageSmoothingEnabled = true;
}

// ★ 렌더 프레임 루프용: 소스가 그려진 fit-rect(ox,oy,rw,rh) 안에 마스크들을 시각 ct에 적용.
//   region 좌표는 기본이 소스 영상 기준 정규화(0..1) → fit-rect로 매핑.
//   🎞 **cover(#2855 QA ②)에서는 fit-rect 가 캔버스를 ★넘칠 수 있다★**(ox/oy 가 음수). 그래서 소스
//      마스크의 픽셀 좌표 정확성은 `drawImage` 가 목적 사각형 밖 픽셀을 ★소스 사각형에 비례해
//      클리핑★ 한다는 spec 동작에 의존한다 — 넘친 영역의 마스크는 화면 밖이라 안 보일 뿐이고,
//      보이는 영역의 좌표는 그대로 정합이다. 이 전제가 깨지면 크롭 컷의 모자이크가 밀린다.
//   🩹 space:"canvas" 마스크(self-verify bbox)는 합본 프레임(캔버스) 기준이라 fit-rect가 아니라
//   캔버스 전체 좌표로 매핑해야 정합(레터박스 오프셋 봉합). canvas 인자를 넘겨야 활성(미전달=기존대로 fit-rect).
export function applyMasks(
  ctx: CanvasRenderingContext2D, masks: MaskRegion[] | undefined, ct: number,
  ox: number, oy: number, rw: number, rh: number,
  canvas?: { w: number; h: number },
  strict=false,
) {
  if (!masks || !masks.length) return;
  for (const m of masks) {
    const r = regionRectAt(m, ct);
    if (!r) continue;
    // self-verify(캔버스 좌표) vs reedit/수동(소스 fit-rect 좌표) — 마스크별 기준 rect 선택.
    const canvasSpace = m.space === "canvas" && !!canvas;
    const bx = canvasSpace ? 0 : ox, by = canvasSpace ? 0 : oy;
    const bw = canvasSpace ? canvas!.w : rw, bh = canvasSpace ? canvas!.h : rh;
    const ps = Math.max(6, Math.round(bw * 0.03)); // 블록 크기(적용 폭 비례)
    const px = normToPx(r, bx, by, bw, bh);
    pixelate(ctx, px.x, px.y, px.w, px.h, ps, strict);
  }
}

// 원본 하단 자막 가리기용 고정 마스크(소스 영상 하단 밴드).
export function bottomSubtitleMask(): MaskRegion {
  return { type: "fixed", rect: { x: 0.04, y: 0.8, w: 0.92, h: 0.18 } };
}
